import { useState, useSyncExternalStore, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router'
import { api, ApiFehler } from '../api/client'
import type { Benutzer, Pfad } from '../api/typen'
import Brotkrumen, { type Ebene } from './Brotkrumen'
import * as kameraStrom from './kameraStrom'

/** Ein Eintrag im Menü oben rechts. */
export interface Menueeintrag {
  beschriftung: string
  /** Entfällt bei Einträgen mit `eingabe` – dort speichert `eingabe.speichern`. */
  beiKlick?: () => void
  /** Hebt den Eintrag als nicht umkehrbar hervor (Orange laut Styleguide). */
  gefahr?: boolean
  /** Ist dies gesetzt, kommt vor der Ausführung eine Rückfrage im selben Sheet. */
  bestaetigung?: { frage: string; bestaetigungstext: string }
  /** Ein einzelnes Textfeld im selben Sheet, etwa zum Umbenennen. */
  eingabe?: Menueeingabe
}

export interface Menueeingabe {
  feld: string
  wert: string
  hinweis?: string
  /** Ohne dies ist eine leere Eingabe nicht speicherbar. */
  leerErlaubt?: boolean
  /** Das Sheet bleibt offen, bis das Versprechen erfüllt ist, und zeigt einen Fehler darin an. */
  speichern: (wert: string) => Promise<unknown>
}

interface Eigenschaften {
  titel: string
  unter?: string
  /** Ziel des Zurueck-Pfeils; ohne Angabe gibt es keinen Pfeil. */
  zurueck?: string | (() => void)
  children: ReactNode
  fuss?: ReactNode
  breit?: boolean
  /** Pfadleiste unter der Kopfzeile; erlaubt Spruenge ueber mehrere Ebenen. */
  pfad?: Pfad
  ebene?: Ebene
  pfadZusatz?: string
  /** Bildschirmspezifische Menüeinträge; Verwaltung und Abmelden kommen automatisch dazu. */
  menue?: Menueeintrag[]
  /** Für Bildschirme ohne Anmeldung (Anmeldeseite). */
  ohneMenue?: boolean
}

/**
 * Gemeinsames Geruest aller Bildschirme: blauer Kopf, scrollbarer Koerper, feste Fusszeile.
 *
 * <p>In der Fusszeile steht nur die eine Hauptaktion des jeweiligen Schritts - alles Weitere
 * liegt im Menue oben rechts. So bleibt unten Platz fuer den Daumen, und der gefuehrte Ablauf
 * ist immer mit einem Tipp erreichbar.
 */
export default function AppRahmen({
  titel,
  unter,
  zurueck,
  children,
  fuss,
  breit,
  pfad,
  ebene,
  pfadZusatz,
  menue,
  ohneMenue,
}: Eigenschaften) {
  const navigiere = useNavigate()
  const abfragen = useQueryClient()
  const [menueOffen, setMenueOffen] = useState(false)
  const [rueckfrage, setRueckfrage] = useState<Menueeintrag | null>(null)
  const [eingabe, setEingabe] = useState<(Menueeingabe & { titel: string }) | null>(null)
  const [entwurf, setEntwurf] = useState('')
  const [eingabeFehler, setEingabeFehler] = useState<string | null>(null)
  const [speichert, setSpeichert] = useState(false)

  // Die Leuchte überdauert den Kamerabildschirm. Sie muss deshalb von überall aus löschbar sein.
  const blitzAn = useSyncExternalStore(kameraStrom.blitzAbonnieren, kameraStrom.blitzZustand)

  // Erst beim Öffnen des Menüs geladen - der Bildschirm selbst braucht die Angabe nicht.
  const ich = useQuery({
    queryKey: ['ich'],
    queryFn: () => api.get<Benutzer>('/api/auth/me'),
    enabled: menueOffen && !ohneMenue,
    staleTime: 5 * 60 * 1000,
  })

  const abmelden = useMutation({
    mutationFn: () => api.post('/api/auth/logout'),
    onSuccess: () => abfragen.invalidateQueries(),
  })

  const zurueckKlick = () => {
    if (typeof zurueck === 'function') {
      zurueck()
    } else if (typeof zurueck === 'string') {
      navigiere(zurueck)
    }
  }

  const eintraege: Menueeintrag[] = [
    ...(menue ?? []),
    ...(ich.data?.istAdmin ? [{ beschriftung: 'Verwaltung', beiKlick: () => navigiere('/verwaltung') }] : []),
    { beschriftung: 'Abmelden', beiKlick: () => abmelden.mutate() },
  ]

  const schliessen = () => {
    setMenueOffen(false)
    setRueckfrage(null)
    setEingabe(null)
  }

  const ausfuehren = (eintrag: Menueeintrag) => {
    if (eintrag.eingabe) {
      setEingabe({ ...eintrag.eingabe, titel: eintrag.beschriftung })
      setEntwurf(eintrag.eingabe.wert)
      setEingabeFehler(null)
      return
    }
    if (eintrag.bestaetigung) {
      setRueckfrage(eintrag)
      return
    }
    setMenueOffen(false)
    eintrag.beiKlick?.()
  }

  const eingabeSpeichern = async () => {
    if (!eingabe) return
    setSpeichert(true)
    setEingabeFehler(null)
    try {
      await eingabe.speichern(entwurf.trim())
      schliessen()
    } catch (ausnahme) {
      setEingabeFehler(ausnahme instanceof ApiFehler ? ausnahme.message : 'Speichern fehlgeschlagen.')
    } finally {
      setSpeichert(false)
    }
  }

  return (
    <div className={breit ? 'app buero' : 'app'}>
      <header className="appkopf">
        {zurueck !== undefined ? (
          <button className="zurueck" onClick={zurueckKlick} aria-label="Zurück">
            ←
          </button>
        ) : (
          <img className="marke" src="/marke/mark-reversed.svg" alt="" width={32} height={32} />
        )}

        <div className="titel">
          <b>{titel}</b>
          {unter && <span>{unter}</span>}
        </div>

        {blitzAn && (
          <button
            className="blitzknopf"
            onClick={() => void kameraStrom.blitzAusschalten()}
            aria-label="Licht ausschalten"
            title="Licht ausschalten"
          >
            {/* Durchgestrichene Taschenlampe: erscheint nur, solange die Leuchte an ist. */}
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M7 3h10l-2 4H9z" />
              <path d="M10 7h4v11a1 1 0 0 1-1 1h-2a1 1 0 0 1-1-1z" />
              <path d="M4 20 20 4" />
            </svg>
          </button>
        )}

        {!ohneMenue && (
          <button className="menueknopf" onClick={() => setMenueOffen(true)} aria-label="Menü öffnen">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M4 7h16M4 12h16M4 17h16" />
            </svg>
          </button>
        )}
      </header>

      {pfad && ebene && <Brotkrumen pfad={pfad} ebene={ebene} zusatz={pfadZusatz} />}

      <main className="koerper">{children}</main>

      {fuss && <footer className="fuss">{fuss}</footer>}

      {menueOffen && (
        <div className="ueberlagerung" role="dialog" aria-modal="true" onClick={schliessen}>
          <div className="sheet" onClick={(ereignis) => ereignis.stopPropagation()}>
            {eingabe ? (
              <form
                className="gruppe"
                onSubmit={(ereignis) => {
                  ereignis.preventDefault()
                  void eingabeSpeichern()
                }}
              >
                <h3 className="s">{eingabe.titel}</h3>
                <span className="label">{eingabe.feld}</span>
                {eingabe.hinweis && <p className="sek">{eingabe.hinweis}</p>}
                <div className="feld">
                  <input
                    type="text"
                    value={entwurf}
                    autoFocus
                    maxLength={200}
                    aria-label={eingabe.feld}
                    onChange={(ereignis) => setEntwurf(ereignis.target.value)}
                  />
                </div>
                {eingabeFehler && <p className="hinweiszeile">{eingabeFehler}</p>}
                <button
                  type="submit"
                  className="btn btn-primaer"
                  disabled={speichert || (!eingabe.leerErlaubt && entwurf.trim() === '')}
                >
                  Speichern
                </button>
                <button type="button" className="btn-dezent" onClick={() => setEingabe(null)}>
                  Abbrechen
                </button>
              </form>
            ) : rueckfrage ? (
              <>
                <h3 className="s">{rueckfrage.beschriftung}?</h3>
                <p className="sek">{rueckfrage.bestaetigung?.frage}</p>
                <button
                  className="btn btn-primaer"
                  onClick={() => {
                    const eintrag = rueckfrage
                    setRueckfrage(null)
                    setMenueOffen(false)
                    eintrag.beiKlick?.()
                  }}
                >
                  {rueckfrage.bestaetigung?.bestaetigungstext}
                </button>
                <button className="btn-dezent" onClick={() => setRueckfrage(null)}>
                  Abbrechen
                </button>
              </>
            ) : (
              <>
                <h3 className="s">Menü</h3>
                {eintraege.map((eintrag) => (
                  <button
                    key={eintrag.beschriftung}
                    className={eintrag.gefahr ? 'menueeintrag loeschen' : 'menueeintrag'}
                    onClick={() => ausfuehren(eintrag)}
                  >
                    {eintrag.beschriftung}
                  </button>
                ))}
                <button className="btn-dezent" onClick={() => setMenueOffen(false)}>
                  Schließen
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
